import React, { useState } from 'react';
import { NavItem } from 'reactstrap';
import { NavLink } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Button } from 'react-bootstrap';
import { customerSignInRequest } from '../../authConfig';
import { ProfileMenu, TopBar as KaTopBar, TopBarDivider, TopBarSwitcher } from '@ka/webcomponents/nav';
import ConstellationLogo from '../ConstellationLogo';
import DevToolsMenu from './DevToolsMenu';
import ProfileSiteAccess from './ProfileSiteAccess';
import { UserTemplate } from '../UserAccess/UserTemplate';
import { KaEmployeeTemplate } from '../UserAccess/KaEmployeeTemplate';
import { UserBusiness } from '../UserAccess/UserContext';
import { useUser } from '../UserAccess/useUser';
import { useKaEmployee } from '../UserAccess/useKaEmployee';
import { useSelectedBusiness } from '../UserAccess/useSelectedBusiness';
import { usePermission } from '../UserAccess/usePermission';
import Confirm from '../Common/Confirm';

export const NavButton = (props: { text: string, onClick: () => void }) => {
  return (
    <NavItem>
      <Button
        className="btn-ghost-primary"
        variant="link"
        size="sm"
        onClick={props.onClick}>
        <strong>{props.text}</strong>
      </Button>
    </NavItem>
  );
}

export function TopBar() {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const customerSignIn = () => instance.loginRedirect(customerSignInRequest);

  return (
    <KaTopBar
      hasSidebar={isAuthenticated}
      brand={<ConstellationLogo />}>
      {!isAuthenticated && <>
        <NavButton text="Sign In" onClick={customerSignIn} />
      </>}
      <KaEmployeeTemplate>
        <EmployeeSwitcher />
        <TopBarDivider />
        <EmployeeProfileMenu />
      </KaEmployeeTemplate>
      <UserTemplate>
        <UserSwitcher />
        <TopBarDivider />
        <DevToolsMenu />
        <UserProfileMenu />
      </UserTemplate>
    </KaTopBar>
  );
}

const SettingsLinks = () => {
  const { administrator } = useSelectedBusiness();
  const canModifySettings = usePermission('settingsModify');
  if (!canModifySettings) return null;

  return <>
    <div className="dropdown-divider"></div>
    <span className="dropdown-header">Settings</span>
    {administrator && <>
      <NavLink className="btn-ghost-secondary dropdown-item" to="/users"><strong>Users</strong></NavLink>
      <NavLink className="btn-ghost-secondary dropdown-item" to="/sites"><strong>Sites</strong></NavLink>
    </>}
    <NavLink className="btn-ghost-secondary dropdown-item" to="/datasharing"><strong>Data Sharing</strong></NavLink>
    <NavLink className="btn-ghost-secondary dropdown-item" to="/ordersettings"><strong>Orders</strong></NavLink>
    <NavLink className="btn-ghost-secondary dropdown-item" to="/purchaseordersettings"><strong>Purchase Orders</strong></NavLink>
    <NavLink className="btn-ghost-secondary dropdown-item" to="/emailsettings"><strong>Emails</strong></NavLink>
  </>;
}

const UserSwitcher = () => {
  const user = useUser();
  const [businessToSelect, setBusinessToSelect] = useState<UserBusiness | undefined>(undefined);

  const businesses = [...user.businesses]
    .sort((a, b) => prioritizeNonSandboxBusinesses(a, b) || alphabeticallyByName(a, b));

  const changeSelectedBusiness = (business: UserBusiness) => {
    if (business.sandbox && !user.selectedBusiness?.sandbox) {
      setBusinessToSelect(business);
    } else user.setSelectedBusiness(business);
  }

  const confirmSandboxBusiness = (business: UserBusiness) => {
    user.setSelectedBusiness(business);
    setBusinessToSelect(undefined);
  }

  return <>
    <Confirm
      visible={!!businessToSelect}
      title='Switch to Sandbox Mode'
      body={`Do you want to leave the production version of Constellation to enter the sandbox?
              Data entered here will not go to production sites.`}
      isLoading={false}
      onDismiss={() => setBusinessToSelect(undefined)}
      onConfirm={() => { confirmSandboxBusiness(businessToSelect!) }} />
    <TopBarSwitcher
      heading="Business"
      placeholder="Select Business"
      selected={user.selectedBusiness}
      options={businesses}
      toOption={toBusinessOption}
      onSelect={changeSelectedBusiness}>
      {user.selectedBusiness && <SettingsLinks />}
    </TopBarSwitcher>
  </>;
}

const UserProfileMenu = () => {
  const user = useUser();
  const role = user.selectedBusiness?.userRole;

  return (
    <ProfileMenu
      email={user.email}
      onSignOut={user.signOut}
      badges={role && <span className="badge bg-soft-primary text-primary">{role.name}</span>}>
      {user.isDevelopment && user.selectedBusiness && <ProfileSiteAccess />}
    </ProfileMenu>
  );
}

const EmployeeSwitcher = () => {
  const employee = useKaEmployee();
  const businesses = [...employee.businesses].sort(alphabeticallyByName);

  return (
    <TopBarSwitcher
      heading="Business"
      placeholder="Select Business"
      selected={employee.selectedBusiness}
      options={businesses}
      toOption={toBusinessOption}
      onSelect={employee.setSelectedBusiness} />
  );
}

const EmployeeProfileMenu = () => {
  const employee = useKaEmployee();

  return (
    <ProfileMenu
      email={employee.email}
      onSignOut={employee.signOut}
      badges={<span className="badge bg-soft-info text-info">Kahler Employee</span>} />
  );
}

const prioritizeNonSandboxBusinesses = (a: UserBusiness, b: UserBusiness) => Number(a.sandbox) - Number(b.sandbox);

const alphabeticallyByName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);

const toBusinessOption = ({ id, name }: { id: string, name: string }) => ({ id, name });
